import React, { useEffect, useMemo, useState } from "react";
import { Calendar, Plus, Edit, Trash2, X, Clock, Play, BarChart3 } from "lucide-react";
import ActivityLogs from '../components/ActivityLogs';
import Reports from '../components/Reports';

/**
 * PayCycleConfiguration.jsx
 * - Clean Zoho/Keka style admin page
 * - Poppins + Roboto font stack (class used)
 * - Local state CRUD for pay cycles
 * - Drawer (right-hand) for Add / Edit cycle
 * - Test "next run date" calculator
 *
 * Save as: src/pages/PayCycleConfiguration.jsx
 */

export default function PayCycleConfiguration() {
  // Sample pay cycles
  const [cycles, setCycles] = useState(() => [
    {
      id: 1,
      name: "Monthly - 25th Cutoff",
      frequency: "Monthly",
      startDay: 26, // cycle from 26 -> 25 next month
      endDay: 25,
      description: "Payroll run for previous month, cutoff 25th",
      payrollProcessingDay: 28, // day payroll is processed
      active: true,
      assignedTo: "All Employees",
      nextRunDate: computeNextRunDate("Monthly", 28),
      createdAt: new Date().toISOString(),
    },
    {
      id: 2,
      name: "Bi-Weekly - Mon/Fri",
      frequency: "Bi-Weekly",
      startDay: null,
      endDay: null,
      description: "Biweekly pay cycle (Mon-Fri two week span)",
      payrollProcessingDay: null,
      active: false,
      assignedTo: "Contractors",
      nextRunDate: computeNextRunDate("Bi-Weekly"),
      createdAt: new Date().toISOString(),
    },
  ]);

  // UI state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [search, setSearch] = useState("");
  const [filterActive, setFilterActive] = useState("all"); // all / active / inactive
  const [testDateResult, setTestDateResult] = useState(null);
  const [activeTab, setActiveTab] = useState('configuration');
  
  // Pay Cycle Configuration specific activity types
  const activityTypes = [
    "Pay Cycle Created", "Pay Cycle Updated", "Pay Cycle Deleted", 
    "Pay Cycle Activated", "Pay Cycle Deactivated", "Payroll Processed"
  ];

  // Pay Cycle Configuration specific report types
  const reportTypes = [
    "Pay Cycle Configuration Report", "Payroll Processing Report", 
    "Active Pay Cycles Report", "Pay Cycle Assignment Report",
    "Payroll Schedule Report", "Pay Cycle Statistics Report"
  ];

  useEffect(() => {
    // keep nextRunDate updated for active cycles (demo)
    setCycles((prev) => prev.map((c) => ({ ...c, nextRunDate: computeNextRunDate(c.frequency, c.payrollProcessingDay) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helpers
  function emptyForm() {
    return {
      id: null,
      name: "",
      frequency: "Monthly",
      startDay: 1,
      endDay: 30,
      description: "",
      payrollProcessingDay: 1,
      active: true,
      assignedTo: "All Employees",
    };
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setDrawerOpen(true);
  }

  function openEdit(c) {
    setEditing(c);
    setForm({ ...c });
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditing(null);
    setForm(emptyForm());
  }

  function saveCycle(e) {
    e?.preventDefault();
    if (!form.name.trim()) return alert("Please enter cycle name");

    if (editing) {
      setCycles((prev) => prev.map((p) => (p.id === editing.id ? { ...form, nextRunDate: computeNextRunDate(form.frequency, form.payrollProcessingDay) } : p)));
    } else {
      const id = Date.now();
      setCycles((prev) => [{ ...form, id, createdAt: new Date().toISOString(), nextRunDate: computeNextRunDate(form.frequency, form.payrollProcessingDay) }, ...prev]);
    }
    closeDrawer();
  }

  function deleteCycle(id) {
    if (!window.confirm("Delete this pay cycle?")) return;
    setCycles((prev) => prev.filter((c) => c.id !== id));
  }

  function computeNextRunDate(frequency, processingDay = null) {
    const now = new Date();
    if (frequency === "Monthly") {
      const year = now.getFullYear();
      const month = now.getMonth();
      // choose next month if processingDay already passed
      const candidate = new Date(year, month, processingDay || 1);
      if (candidate <= now) {
        return new Date(year, month + 1, processingDay || 1).toISOString();
      }
      return candidate.toISOString();
    }
    if (frequency === "Bi-Weekly") {
      // next Friday of the next 2-week window (demo)
      const d = new Date(now);
      d.setDate(d.getDate() + (14 - (d.getDay())));
      return d.toISOString();
    }
    if (frequency === "Weekly") {
      const d = new Date(now);
      d.setDate(d.getDate() + (7 - d.getDay()));
      return d.toISOString();
    }
    return new Date(now).toISOString();
  }

  // Test run calculator (user-specified)
  function calculateNextRunForPreview(freq, processingDay) {
    const raw = computeNextRunDate(freq, processingDay);
    setTestDateResult(new Date(raw).toLocaleString());
  }

  const filtered = useMemo(() => {
    return cycles.filter((c) => {
      if (filterActive === "active" && !c.active) return false;
      if (filterActive === "inactive" && c.active) return false;
      if (!search.trim()) return true;
      return c.name.toLowerCase().includes(search.toLowerCase()) || (c.description || "").toLowerCase().includes(search.toLowerCase());
    });
  }, [cycles, search, filterActive]);

  // Render
  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8 font-['Poppins','Roboto',sans-serif]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-3">
              <Calendar className="text-teal-600" /> Pay Cycle Configuration
            </h1>
            <p className="text-sm text-gray-500 mt-1">Define payroll cycles (monthly, bi-weekly, weekly), processing days and assignment.</p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center bg-white border rounded px-3 py-2">
              <input placeholder="Search cycles..." className="outline-none text-sm" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>

            <select className="border rounded p-2 text-sm" value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <button onClick={openCreate} className="flex items-center gap-2 px-3 py-1 rounded-md bg-teal-600 text-white text-sm">
              <Plus size={14} /> New Cycle
            </button>
            
            <button 
              onClick={() => setActiveTab('activity')} 
              className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-colors ${
                activeTab === 'activity' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              <Clock size={14} /> Activity Logs
            </button>
            
            <button 
              onClick={() => setActiveTab('reports')} 
              className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-colors ${
                activeTab === 'reports' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              <BarChart3 size={14} /> Reports
            </button>
            
            <button 
              onClick={() => setActiveTab('configuration')} 
              className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm transition-colors ${
                activeTab === 'configuration' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
            >
              <Calendar size={14} /> Configuration
            </button>
          </div>
        </div>

        {/* Activity Logs Tab */}
        {activeTab === 'activity' && (
          <ActivityLogs 
            moduleName="Pay Cycle Configuration" 
            activityTypes={activityTypes} 
          />
        )}
        
        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <Reports 
            moduleName="Pay Cycle Configuration" 
            reportTypes={reportTypes} 
          />
        )}

        {/* Configuration Tab */}
        {activeTab === 'configuration' && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-4">
            {filtered.map((c) => (
              <div key={c.id} className="bg-white p-4 rounded-lg shadow flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold text-gray-800">{c.name} {c.active ? <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded ml-2">Active</span> : <span className="text-xs bg-gray-50 text-gray-700 px-2 py-0.5 rounded ml-2">Inactive</span>}</div>
                  <div className="text-sm text-gray-500 mt-1">{c.description}</div>
                  <div className="text-xs text-gray-500 mt-2">Frequency: <strong className="text-gray-800">{c.frequency}</strong> • Next Run: <strong>{c.nextRunDate ? new Date(c.nextRunDate).toLocaleDateString() : "—"}</strong></div>
                </div>

                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(c)} className="text-indigo-600"><Edit size={16} /></button>
                  <button onClick={() => deleteCycle(c.id)} className="text-red-600"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}

            {filtered.length === 0 && (
              <div className="bg-white p-6 rounded-lg shadow text-center text-gray-500">No pay cycles found.</div>
            )}
          </div>

          <aside className="space-y-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-700">Quick Tools</h4>
                <div className="text-xs text-gray-500">{cycles.length} cycles</div>
              </div>

              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={14} /> <button onClick={() => alert("Trigger manual payroll (demo)")} className="text-left text-sm">Trigger Manual Run (demo)</button>
                </div>
                <div className="flex items-center gap-2">
                  <Play size={14} /> <button onClick={() => alert("Simulate scheduled run (demo)")}>Simulate Schedule</button>
                </div>
                <div>
                  <button onClick={() => { setCycles([]); }} className="text-sm text-red-600">Clear All (demo)</button>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="text-sm font-semibold text-gray-700">Test Next Run</h4>
              <p className="text-xs text-gray-500 mt-1">Pick frequency & processing day to preview the next run date.</p>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <select id="freq" defaultValue="Monthly" className="border rounded p-2 text-sm" onChange={(e) => { /* no-op */ }}>
                  <option>Monthly</option>
                  <option>Bi-Weekly</option>
                  <option>Weekly</option>
                </select>
                <input type="number" defaultValue={1} min={1} max={31} className="border rounded p-2 text-sm" id="testProcDay" />
                <button onClick={() => {
                  const freqEl = document.getElementById("freq");
                  const pd = Number(document.getElementById("testProcDay").value || 1);
                  calculateNextRunForPreview(freqEl.value, pd);
                }} className="col-span-2 mt-2 px-3 py-2 bg-teal-600 text-white rounded text-sm flex items-center gap-2 justify-center"><Clock size={14}/> Calculate</button>
              </div>

              {testDateResult && <div className="mt-3 text-sm text-gray-700">Next run: <strong>{testDateResult}</strong></div>}
            </div>
          </aside>
        </div>
        )}
      </div>

      {/* Drawer */}
      {drawerOpen && (
        <Drawer onClose={closeDrawer} title={editing ? "Edit Pay Cycle" : "Create Pay Cycle"}>
          <form onSubmit={saveCycle} className="space-y-4">
            <div>
              <label className="text-sm text-gray-700">Cycle Name</label>
              <input className="mt-1 w-full border rounded p-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-700">Frequency</label>
                <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="mt-1 w-full border rounded p-2">
                  <option>Monthly</option>
                  <option>Bi-Weekly</option>
                  <option>Weekly</option>
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-700">Payroll Processing Day (day of month)</label>
                <input type="number" min={1} max={31} value={form.payrollProcessingDay} onChange={(e) => setForm({ ...form, payrollProcessingDay: Number(e.target.value) })} className="mt-1 w-full border rounded p-2" />
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-700">Assigned To</label>
              <input value={form.assignedTo} onChange={(e) => setForm({ ...form, assignedTo: e.target.value })} className="mt-1 w-full border rounded p-2" />
            </div>

            <div>
              <label className="text-sm text-gray-700">Description</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full border rounded p-2"></textarea>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
              <div className="ml-auto text-xs text-gray-500">Created: {editing ? new Date(editing.createdAt).toLocaleDateString() : "—"}</div>
            </div>

            <div className="flex justify-end gap-3">
              <button type="button" onClick={closeDrawer} className="px-3 py-1 border rounded">Cancel</button>
              <button type="submit" className="px-4 py-1 bg-teal-600 text-white rounded">Save Cycle</button>
            </div>
          </form>
        </Drawer>
      )}
    </div>
  );
}

/* Drawer component */
function Drawer({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-black bg-opacity-40 z-40" />
      <aside className="fixed right-0 top-0 h-full w-full md:w-2/5 bg-white z-50 shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="p-2 rounded hover:bg-gray-100"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </aside>
    </>
  );
}
